import { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { useAuthService, isEntraAuthResponse } from "../../services/auth.service";
import { Link, useNavigate } from "react-router-dom";
import { useAuthContext } from "../../AuthProvider";
import { handleError } from "../../common/utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowAltCircleRight, faArrowAltCircleLeft } from "@fortawesome/free-regular-svg-icons";
import { InlineSpinner } from "../../components/inline-spinner";
import { AuthProvider, Roles, UserDto } from "../../types/user.types";
import { loginWithEntraId } from "../../services/msal.service";
import debounce from "lodash/debounce";

interface LoginForm {
  email: string;
  password: string;
}

type AuthCheckStatus = "idle" | "checking" | "entra" | "local";

// Check if Entra ID is configured
const isEntraConfigured = () => {
  return !!(process.env.REACT_APP_ENTRA_INTERNAL_TENANT_ID || process.env.REACT_APP_ENTRA_EXTERNAL_TENANT_ID);
};

// Email validation regex
const isValidEmail = (email: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const Login = () => {
  const { setAuthenticatedUser } = useAuthContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [authCheckStatus, setAuthCheckStatus] = useState<AuthCheckStatus>(
    isEntraConfigured() ? "idle" : "local"
  );
  const [checkedEmail, setCheckedEmail] = useState("");
  const [entraProvider, setEntraProvider] = useState<AuthProvider | null>(null);
  const [showCheckingSpinner, setShowCheckingSpinner] = useState(false);
  const requestIdRef = useRef(0);
  const spinnerTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { login, getAuthMethod } = useAuthService();

  // Derived state: show password field when not Entra configured, local auth, or re-checking (to avoid layout shift)
  const showPasswordField = !isEntraConfigured() || authCheckStatus === "local" || authCheckStatus === "checking";
  const isChecking = authCheckStatus === "checking";

  const navigateByRole = (roleId: number) => {
    if (roleId === Roles.Admin) {
      navigate("/admin");
    } else if (roleId === Roles.Manager) {
      navigate("/manager");
    } else if (roleId === Roles.Reviewer) {
      navigate("/work");
    }
  };

  // Clear spinner timeout helper
  const clearSpinnerTimeout = useCallback(() => {
    if (spinnerTimeoutRef.current) {
      clearTimeout(spinnerTimeoutRef.current);
      spinnerTimeoutRef.current = null;
    }
    setShowCheckingSpinner(false);
  }, []);

  // Check auth method with race condition handling
  const checkAuthMethod = useCallback(
    async (email: string) => {
      const currentRequestId = ++requestIdRef.current;
      setAuthCheckStatus("checking");
      setErrorMessage("");

      // Delay showing spinner to avoid flash on fast responses (150ms threshold)
      clearSpinnerTimeout();
      spinnerTimeoutRef.current = setTimeout(() => {
        // Only show spinner if still checking (same request)
        if (currentRequestId === requestIdRef.current) {
          setShowCheckingSpinner(true);
        }
      }, 150);

      try {
        const authMethod = await getAuthMethod(email);

        // Ignore stale responses
        if (currentRequestId !== requestIdRef.current) return;

        clearSpinnerTimeout();
        setCheckedEmail(email);
        if (authMethod?.useEntraAuth) {
          setEntraProvider(authMethod.provider);
          setAuthCheckStatus("entra");
        } else {
          setEntraProvider(null);
          setAuthCheckStatus("local");
        }
      } catch (error) {
        // Ignore stale responses
        if (currentRequestId !== requestIdRef.current) return;
        // Fallback to local auth on error
        clearSpinnerTimeout();
        setEntraProvider(null);
        setAuthCheckStatus("local");
      }
    },
    [getAuthMethod, clearSpinnerTimeout]
  );

  // Debounced version of checkAuthMethod (300ms delay)
  const debouncedCheckAuthMethod = useMemo(
    () => debounce((email: string) => checkAuthMethod(email), 300),
    [checkAuthMethod]
  );

  // Cleanup debounced function and spinner timeout on unmount
  useEffect(() => {
    return () => {
      debouncedCheckAuthMethod.cancel();
      clearSpinnerTimeout();
    };
  }, [debouncedCheckAuthMethod, clearSpinnerTimeout]);

  // Handle email field changes with debounced auth check
  const handleEmailChange = useCallback(
    (email: string, setFieldValue: (field: string, value: string) => void) => {
      setFieldValue("email", email);

      // Skip if Entra not configured (always local auth)
      if (!isEntraConfigured()) return;

      // If email changed from the last checked email and we've already done a check
      if (email !== checkedEmail && authCheckStatus !== "idle") {
        if (isValidEmail(email)) {
          debouncedCheckAuthMethod(email);
        } else {
          // Cancel pending check if email becomes invalid
          debouncedCheckAuthMethod.cancel();
          // Don't reset status - keep showing current UI until valid email entered
        }
      }
    },
    [checkedEmail, authCheckStatus, debouncedCheckAuthMethod]
  );

  // Helper to safely redirect to Entra ID with error handling
  const safeLoginWithEntraId = async (provider: AuthProvider) => {
    try {
      await loginWithEntraId(provider);
      // Note: Page will redirect, so no need to set loading to false
    } catch (entraError) {
      setLoading(false);
      setErrorMessage("Failed to redirect to Entra ID. Please try again.");
    }
  };

  const handleLogin = async (formValues: LoginForm) => {
    const { email, password } = formValues;

    // Cancel any pending debounced check to prevent race conditions
    debouncedCheckAuthMethod.cancel();

    setErrorMessage("");
    setLoading(true);

    try {
      // Handle based on current auth check status
      if (authCheckStatus === "idle") {
        // First submission - check auth method
        const authMethod = await getAuthMethod(email);
        setCheckedEmail(email);

        if (authMethod?.useEntraAuth) {
          // Redirect to Entra ID immediately
          await safeLoginWithEntraId(authMethod.provider);
          return;
        }

        // User uses local auth - show password field
        setEntraProvider(null);
        setAuthCheckStatus("local");
        setLoading(false);
        return;
      }

      if (authCheckStatus === "entra") {
        // User confirmed as Entra - redirect to Entra ID
        if (entraProvider) {
          await safeLoginWithEntraId(entraProvider);
          return;
        }
        // Fallback: re-check if provider not stored
        const authMethod = await getAuthMethod(email);
        if (authMethod?.useEntraAuth) {
          await safeLoginWithEntraId(authMethod.provider);
          return;
        }
        // Re-check returned local auth - update state and show password field
        setAuthCheckStatus("local");
        setLoading(false);
        return;
      }

      // Local authentication with password
      const response = await login(email, password);

      if (!response) {
        setErrorMessage("Login failed");
        setLoading(false);
        return;
      }

      // Check if user requires Entra ID authentication (fallback for non-Entra configured environments)
      if (isEntraAuthResponse(response)) {
        // Redirect to Entra ID
        await safeLoginWithEntraId(response.provider);
        return;
      }

      // Local authentication succeeded
      setAuthenticatedUser(response as UserDto);
      setLoading(false);
      navigateByRole(response.roleId);
    } catch (error) {
      handleError(error, setErrorMessage, setLoading);
    }
  };

  return (
    <div className="card card-container tablet-left">
      <h2>Sign In</h2>
      <Formik
        initialValues={{ email: "", password: "" } as LoginForm}
        validate={(values) => {
          const errors = {} as any;
          if (values.email === "") {
            errors.email = "Email is required";
          } else if (!isValidEmail(values.email)) {
            errors.email = "Please enter a valid email address";
          }
          if (showPasswordField && values.password === "") {
            errors.password = "Password is required";
          }
          return errors;
        }}
        onSubmit={handleLogin}
      >
        {({ errors, touched, setFieldValue, setValues, setErrors, setTouched }) => (
          <Form>
            <div className="form-group mt-4 mb-2 position-relative">
              <Field
                name="email"
                type="text"
                placeholder="Email"
                className="form-control"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleEmailChange(e.target.value, setFieldValue)
                }
              />
              {showCheckingSpinner && (
                <span className="input-spinner-overlay">
                  <InlineSpinner />
                </span>
              )}
              <ErrorMessage name="email" component="div" className="white" />
            </div>
            {showPasswordField && (
              <div className="form-group mb-2">
                <Field name="password" type="password" placeholder="Password" className="form-control" autoFocus />
                {/* Only show password error when field has been touched - resetForm clears touched state */}
                {touched.password && errors.password && <div className="white">{errors.password}</div>}
              </div>
            )}
            {errorMessage && (
              <div className="form-group mb-2">
                <div className="white" role="alert">
                  {errorMessage}
                </div>
              </div>
            )}
            <div className="form-group mt-4">
              <button
                type="submit"
                className="btn btn-light btn-outline"
                disabled={loading || isChecking || !!errors.email || (showPasswordField && !!errors.password)}
              >
                {loading && <InlineSpinner />}
                <FontAwesomeIcon icon={faArrowAltCircleRight} className="me-2" />
                {authCheckStatus === "entra"
                  ? "Sign In with Entra"
                  : showPasswordField
                    ? "Sign In"
                    : "Continue"}
              </button>
            </div>

            {showPasswordField && (
              <div className={`mt-3 d-flex align-items-center ${isEntraConfigured() ? "justify-content-between" : "justify-content-end"}`}>
                {isEntraConfigured() && (
                  <button
                    type="button"
                    className="btn btn-link small white p-0 text-decoration-none"
                    style={{ color: "inherit" }}
                    onMouseDown={() => {
                      // Use onMouseDown instead of onClick to fire before blur causes layout shift
                      setAuthCheckStatus("idle");
                      setCheckedEmail("");
                      setEntraProvider(null);
                      debouncedCheckAuthMethod.cancel();
                      clearSpinnerTimeout();
                      setValues({ email: "", password: "" }, false);
                      setTouched({}, false);
                      setErrors({});
                    }}
                  >
                    <FontAwesomeIcon icon={faArrowAltCircleLeft} className="me-1" />
                    Back
                  </button>
                )}
                <Link className="small white" to="/forgot-password">
                  Forgot your password?
                </Link>
              </div>
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default Login;
